import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

interface Property {
  id: number;
  images: string[];
  title: string;
  price: number;
  location: string;
  propertyType: string;
  bedrooms: number;
  berth: number;
  distance: number;
  area: string;
  condition: string;
  description: string;
  features: string[];
  parkName: string;
  yearBuilt: string;
  fullDescription: string;
}

type SubmitStatus = 'idle' | 'success' | 'error';

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    address: '',
    telephone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const properties: Property[] = [
    {
      id: 1,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e11f6f89c1e18f9e5ad59f12a35fad01.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/ec9ea709c0afb0519f0bc8f9a4619e20.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2ce5299af57f1c17c13cc170429fe7d2.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2cf9f18debf3a15367ed0089eb737dac.jpeg'
      ],
      title: 'ABI Ambleside Premier Lodge',
      price: 69950,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'Lodge',
      bedrooms: 2,
      berth: 6,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'New',
      description: 'A spacious, luxury holiday lodge featuring contemporary open-plan living, premium integrated kitchen appliances, stylish interiors and outdoor decking — designed for year-round comfort and relaxed entertaining',
      features: ['Double Glazing', 'En-Suite Shower', 'Fully Furnished', 'Driveway Parking', 'Countryside Views', 'Exclusive Development', 'Fully Residential', 'Waterside', 'Low Energy Costs'],
      parkName: 'Briarwood Development, Country Meadows',
      yearBuilt: '2025',
      fullDescription: 'Welcome to this stunning ABI Ambleside Premier Lodge, located on the exclusive Briarwood Development of Country Meadows. This beautiful holiday home offers the perfect blend of luxury, comfort, and tranquility in a picturesque lakeside setting.\n\nThe lodge is situated on a quiet retreat surrounded by sweeping lakes which flow through the park, standing proudly on a prominent lakeside plot. This prime location offers breathtaking water views and a peaceful atmosphere, perfect for relaxation and enjoying the natural beauty of the surroundings.\n\nThe spacious open-plan living area creates an airy and welcoming feel throughout the lodge. The contemporary design features premium integrated kitchen appliances, stylish interiors, and modern finishes that make this property perfect for year-round comfort and relaxed entertaining.\n\nThe master bedroom is a true sanctuary, featuring a king-size bed with bedside cupboards and a separate dressing area housing a fitted wardrobe and dressing table. The en-suite offers an enclosed shower, WC, basin, and cupboards for added convenience and privacy.\n\nThe second bedroom provides comfortable accommodation with two full-size single beds, chest of drawers, bedside cupboard, and fitted wardrobe - ideal for family or guests. The main bathroom is generously sized with shower, WC, basin, and cupboards.\n\nThe property comes with its own dedicated parking space next to the lodge, and decking packages can be arranged at competitive prices to enhance your outdoor living space.\n\nThis is an exceptional opportunity to own a premium holiday home on an exclusive development with well-stocked fishing lakes, close to the beach, and in a family-friendly environment. The site is open from March to the end of November each year.\n\nSite fees are approximately £2,895 per year (2026 fees included with purchase), which covers water and sewerage with no council tax payable. This represents excellent value for a fully residential leisure licence property with low energy costs.\n\nFor more information, please contact us on 01754 874455.'
    },
    {
      id: 2,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c30c2c3c760cc9a8c1d68013e3f30226.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c7c277625a388f7b7e701f77f66f7c65.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/1b7948d5ab66dbb305bd0104ca6db2c0.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2a7eddd40bc7005eb8d5df04a8702413.jpeg'
      ],
      title: 'ABI Coworth Deluxe',
      price: 35450,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'Static Caravan',
      bedrooms: 2,
      berth: 6,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'Good',
      description: 'A stylish 2022 ABI Cosworth Deluxe, offering premium craftsmanship, a spacious open-plan layout, and high-quality furnishings for comfortable holiday living.',
      features: ['Double Glazing', 'En-Suite Shower', 'Fully Furnished', 'Driveway Parking', 'Countryside Views', 'Exclusive Development', 'Fully Residential', 'Waterside', 'Low Energy Costs'],
      parkName: 'Country Meadows Holiday Park',
      yearBuilt: '2022',
      fullDescription: 'Welcome to this stylish 2022 ABI Coworth Deluxe, located on the beautiful Country Meadows Holiday Park. This premium static caravan offers exceptional quality, comfort, and style, making it the perfect holiday retreat for families seeking a peaceful coastal getaway.\n\nThe Holiday Home is located on our Country Meadows Holiday Park just a short walk from the local attractions, restaurants and bars as well as the beautiful sandy beach. It is a quiet and tranquil retreat boasting open countryside views, situated in a popular and quiet corner of the park.\n\nThe property comes with its own parking space next to the holiday home and features decking to the side and front with modern glass balustrades, creating a stylish outdoor living space perfect for relaxing and entertaining.\n\nThe open-plan living area offers a spacious and airy feel throughout the caravan. The contemporary design features high-quality furnishings, a well-equipped kitchen, and comfortable seating areas that make this property perfect for family holidays and relaxed entertaining.\n\nThe accommodation comprises two well-appointed bedrooms. The main bedroom features a king-size bed, en-suite shower room, and ample storage throughout, providing a private sanctuary for relaxation. The second bedroom has two full size single beds also with plenty of storage, ideal for children or guests.\n\nThis 2022 model is in excellent condition and benefits from double glazing throughout, ensuring comfort in all seasons. The premium specification includes modern fixtures and fittings, creating a stylish and contemporary holiday home.\n\nThe site is open from March to end of November each year. Site fees are payable of approximately £2,895 per year, but 2026\'s fees are included with this Holiday Home. This includes water and sewerage and NO council tax payable.\n\nTenure: Leisure Licence\nCouncil Tax: None\nGround Rent/Service Charges: £2,895 per year\n\nFor more information, please contact us on 01754 874455.'
    },
    {
      id: 3,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6f8a9a968df10b41022c03a776d47b5f.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/1e1bf90856d537cadeca69718df2631d.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/5e92bbbde0a0a5062cddbb1c83467902.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/3d57d8f485eef6b47e539eb15141ec1d.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/91566028a899a721605214acdac65abe.jpeg'
      ],
      title: 'ABI Beaumont Lodge',
      price: 56950,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'static caravan',
      bedrooms: 3,
      berth: 8,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'Good',
      description: 'A well-appointed 2020 ABI Beaumont Lodge combining elegant design, generous living space, and quality finishes for relaxed, luxurious holiday park living.',
      features: ['Full Wrap Around Decking', 'Patio Area', 'Double Glazing', 'Fully Furnished', 'Fully Residential', 'Close to Beach', 'Amazing Views', 'Tranquil Park', 'Waterside'],
      parkName: 'Briarwood',
      yearBuilt: '2020',
      fullDescription: 'Welcome to this stunning ABI Beaumont Lodge, located on the exclusive Briarwood development. This beautiful holiday home offers the perfect blend of luxury, comfort, and tranquility in a picturesque lakeside setting.\n\nIt is a quiet and tranquil retreat boasting stunning lakeside and open countryside views. The lodge stands on the edge of our stunning new Briarwood Lake, offering breathtaking water views and a peaceful atmosphere perfect for relaxation.\n\nThe property comes with its own double parking space next to the lodge and decking to the side and front with modern glass balustrades, creating a stylish outdoor living space perfect for relaxing and entertaining.\n\nOpen plan living offers a spacious and airy feel to the lodge. The contemporary design features high-quality furnishings, a well-equipped kitchen, and comfortable seating areas that make this property perfect for family holidays and relaxed entertaining.\n\nThree bedrooms provide generous accommodation. The main bedroom features a king-size bed, en-suite shower room and ample storage throughout, providing a private sanctuary for relaxation. The 2nd and 3rd bedrooms have two full size single beds each, both just across the hallway from the master shower room - ideal for children or guests.\n\nThis is an exceptional opportunity to own a premium holiday home on an exclusive development with well-stocked fishing lakes, close to the beach, and in a family-friendly environment.\n\nThe site is open from March to end of November each year. Site fees are payable of approximately £2,895 per year but 2026\'s fees are included with this Holiday Home. This includes water and sewerage and NO council tax payable.\n\nTenure: Leisure Licence\nCouncil Tax: None\nGround Rent/Service Charges: £2,895 per year\n\nFor more information, please contact us on 01754 874455.'
    },
    {
      id: 4,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c44304d6037f1eec1178f95b1f36e4a6.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6d7c89184cce1d8ab4f6e78cb8d90057.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/559f1c149bd93656b4f25da6cbd395fc.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/99c62cb911adc75e2a81d443bbedf7cd.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/bb7929e7952348c9b64c270903c9f8f5.jpeg'
      ],
      title: 'Willerby Malton',
      price: 45000,
      location: 'Sutton-on-Sea, Lincolnshire',
      propertyType: 'Static Caravan',
      bedrooms: 2,
      berth: 6,
      distance: 4,
      area: 'Sutton-on-Sea',
      condition: 'New',
      description: 'Escape to the countryside with this brand new 2 bedroom Willerby Malton holiday home at Poplar Farm Leisure Park.',
      features: ['Waterside', 'Driveway Parking', 'Double Glazing', 'Exclusive Development', 'Dog Friendly Park', 'Close to the Beach', 'Fully Furnished', 'Amazing Views', 'Great Owners Benefits'],
      parkName: 'Poplar Farm Leisure Park',
      yearBuilt: '2025',
      fullDescription: 'Escape to the countryside with this brand new 2 bedroom Willerby Malton holiday home at Poplar Farm Leisure Park.\n\n• Modern open plan living and kitchen\n• 2 cosy bedrooms\n• Large windows with stunning countryside views\n• Private wraparound decking – perfect for relaxing evenings watching the sun go down\n\nJust a 15 minute walk to Sandilands stunning beach, with excellent facilities on your doorstep. Relax and unwind at our licenced onsite coffee shop, where you will find great food, drinks and a welcoming atmosphere.\n\nThis brand new 2025 Willerby Malton offers contemporary design and quality finishes throughout. The open-plan living space creates a bright and airy atmosphere, perfect for family gatherings and entertaining. The modern kitchen is well-equipped with integrated appliances and ample storage.\n\nBoth bedrooms are thoughtfully designed with comfortable furnishings and plenty of storage space. Large windows throughout the property flood the interior with natural light and showcase the beautiful countryside views.\n\nThe private wraparound decking is a real highlight, providing the perfect outdoor space for al fresco dining, morning coffee, or simply relaxing and watching the sunset over the countryside.\n\nFor more information, please contact us on 07454 666 892.'
    },
    {
      id: 5,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/5e9dd5df316144dce00e5aa48a659187.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/53a32db37ca5958d68f59ef3cbabcfb0.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/eba40af4fe594a9e70a8a20031996100.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/7eaba741833aeafb14dfe649207a9ba6.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2b329b3eeed4a8f9f12c98f54d0e1ef4.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/fcc102934bf7ac18b6de24fbd669af76.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2a05b73f01a9800b6aeff8c90b8ea68a.jpeg'
      ],
      title: 'Willerby Manor',
      price: 54950,
      location: 'Sutton-on-Sea',
      propertyType: 'Static Caravan',
      bedrooms: 2,
      berth: 6,
      distance: 4,
      area: 'Sutton-on-Sea',
      condition: 'New',
      description: 'A brand new Willerby Manor with elegant styling, spacious living areas, and premium finishes designed for relaxed, luxurious holiday living',
      features: ['Waterside', 'Driveway Parking', 'Double Glazing', 'Exclusive Development', 'Dog Friendly Park', 'Close to the Beach', 'Fully Furnished', 'Full Wrap Around Decking', 'Great Owners Benefits'],
      parkName: 'Poplar Farm Leisure Park',
      yearBuilt: '2025',
      fullDescription: 'Escape to the countryside with this brand new 2 bedroom Willerby Manor holiday home at Poplar Farm Leisure Park.\n\n• Modern open plan living and kitchen\n• 2 cosy bedrooms\n• Large windows with stunning countryside views\n• Private wraparound decking – perfect for relaxing evenings watching the sun go down\n\nJust a 15 minute walk to Sandilands stunning beach, with excellent facilities on your doorstep. Relax and unwind at our licenced onsite coffee shop, where you will find great food, drinks and a welcoming atmosphere.\n\nThis brand new 2025 Willerby Manor offers elegant styling and premium finishes throughout. The spacious open-plan living area creates a bright and airy atmosphere, perfect for family gatherings and entertaining. The modern kitchen is well-equipped with integrated appliances and ample storage.\n\nBoth bedrooms are thoughtfully designed with comfortable furnishings and plenty of storage space. Large windows throughout the property flood the interior with natural light and showcase the beautiful countryside views.\n\nThe private wraparound decking is a real highlight, providing the perfect outdoor space for al fresco dining, morning coffee, or simply relaxing and watching the sunset over the countryside.\n\nFor more information, please contact us on 07454 666 892.'
    }
  ];

  const property = properties.find(p => p.id === parseInt(id || '0'));

  useEffect(() => {
    if (!property) {
      navigate('/leisure-home-sales');
    }
  }, [property, navigate]);

  if (!property) {
    return null;
  }

  const relatedProperties = properties
    .filter(p => p.id !== property.id && (p.area === property.area || p.propertyType === property.propertyType))
    .slice(0, 3);

  const handleOpenEnquiry = () => {
    setShowEnquiryForm(true);
    setSubmitStatus('idle');
    document.body.style.overflow = 'hidden';
  };

  const handleCloseEnquiry = () => {
    setShowEnquiryForm(false);
    setFormData({
      customerName: '',
      address: '',
      telephone: '',
      email: ''
    });
    setSubmitStatus('idle');
    document.body.style.overflow = 'auto';
  };

  const handleSubmitEnquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.address || !formData.telephone || !formData.email) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('customerName', formData.customerName);
      formBody.append('address', formData.address);
      formBody.append('telephone', formData.telephone);
      formBody.append('email', formData.email);
      formBody.append('propertyTitle', property.title);
      formBody.append('propertyPrice', `£${property.price.toLocaleString()}`);
      formBody.append('propertyLocation', property.location);
      formBody.append('propertyType', property.propertyType);
      formBody.append('recipientEmail', 'leisure@lincolnshireadworks.co.uk');

      const response = await fetch('https://readdy.ai/api/form/d5jbq2mihetfqh7m5njg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          handleCloseEnquiry();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  const printPage = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://seasidevouchers.co.uk/wp-content/uploads/2024/11/Group-1000005662-1024x974.png" 
                alt="Seaside Vouchers" 
                className="h-16 w-16"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-10"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Home</Link>
              <Link to="/how-it-works" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">How it works</Link>
              <Link to="/seaside-offers" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Seaside Vouchers App</Link>
              <Link to="/leisure-home-sales" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Leisure Home Sales</Link>
              <Link to="/accommodation" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Accommodation</Link>
              <Link to="/contact" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Contact us</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-32 pb-6 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-cyan-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/leisure-home-sales" className="hover:text-cyan-600 transition-colors cursor-pointer">Leisure Home Sales</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">{property.title}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Property Header */}
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">{property.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-home-4-line text-cyan-600"></i>
                  {property.parkName}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line text-cyan-600"></i>
                  {property.location}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-building-line text-cyan-600"></i>
                  {property.propertyType}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-hotel-bed-line text-cyan-600"></i>
                  {property.bedrooms} bedrooms
                </span>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="mb-8">
              <div className="relative w-full h-[500px] mb-4 rounded-lg overflow-hidden group">
                <img 
                  src={property.images[currentImageIndex]} 
                  alt={`${property.title} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover object-center"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={() => setCurrentImageIndex((currentImageIndex - 1 + property.images.length) % property.images.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-3xl"></i>
                </button>
                <button
                  onClick={() => setCurrentImageIndex((currentImageIndex + 1) % property.images.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-3xl"></i>
                </button>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                  {currentImageIndex + 1} / {property.images.length}
                </div>
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-6 gap-2">
                {property.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative w-full h-20 rounded-md overflow-hidden cursor-pointer transition-all ${
                      index === currentImageIndex ? 'ring-4 ring-cyan-500' : 'hover:opacity-75'
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Key Features */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 mb-8 border border-cyan-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {property.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab('description')}
                  className={`pb-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'description' 
                      ? 'text-cyan-600 border-b-2 border-cyan-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`pb-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'details' 
                      ? 'text-cyan-600 border-b-2 border-cyan-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Property Details
                </button>
                <button
                  onClick={() => setActiveTab('park')}
                  className={`pb-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'park' 
                      ? 'text-cyan-600 border-b-2 border-cyan-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Park Information
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="mb-8">
              {activeTab === 'description' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {property.fullDescription}
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Details</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Property Type</p>
                      <p className="font-semibold text-gray-900">{property.propertyType}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Bedrooms</p>
                      <p className="font-semibold text-gray-900">{property.bedrooms}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Berth</p>
                      <p className="font-semibold text-gray-900">{property.berth} people</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Condition</p>
                      <p className="font-semibold text-gray-900">{property.condition}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Year Built</p>
                      <p className="font-semibold text-gray-900">{property.yearBuilt}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Distance</p>
                      <p className="font-semibold text-gray-900">{property.distance} miles</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'park' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{property.parkName}</h2>
                  <p className="text-gray-700 mb-4">
                    {property.id === 1 ? (
                      <>
                        The Briarwood Development at Country Meadows is a quiet retreat surrounded by sweeping lakes which flow through the park. This exclusive development offers a peaceful, family-friendly environment with well-stocked fishing lakes and beautiful countryside views.
                        <br /><br />
                        <strong>Site Information:</strong>
                        <br />• Open from March to end of November each year
                        <br />• Site fees: Approximately £2,895 per year (2026 fees included with this Holiday Home)
                        <br />• Includes water and sewerage
                        <br />• No council tax payable
                        <br />• Tenure: Leisure Licence
                        <br />• Fully residential
                        <br /><br />
                        <strong>Contact:</strong> 01754 874455
                      </>
                    ) : property.id === 2 ? (
                      <>
                        Country Meadows Holiday Park is a quiet and tranquil retreat boasting open countryside views. Located just a short walk from local attractions, restaurants and bars as well as the beautiful sandy beach, it offers the perfect balance of peaceful surroundings and convenient access to amenities.
                        <br /><br />
                        <strong>Site Information:</strong>
                        <br />• Open from March to end of November each year
                        <br />• Site fees: Approximately £2,895 per year (2026 fees included with this Holiday Home)
                        <br />• Includes water and sewerage
                        <br />• No council tax payable
                        <br />• Tenure: Leisure Licence
                        <br />• Fully residential
                        <br /><br />
                        <strong>Contact:</strong> 01754 874455
                      </>
                    ) : property.id === 3 ? (
                      <>
                        Briarwood is a quiet and tranquil retreat boasting stunning lakeside and open countryside views. The lodge stands on the edge of the stunning new Briarwood Lake, offering breathtaking water views and a peaceful atmosphere perfect for relaxation.
                        <br /><br />
                        <strong>Site Information:</strong>
                        <br />• Open from March to end of November each year
                        <br />• Site fees: Approximately £2,895 per year (2026 fees included with this Holiday Home)
                        <br />• Includes water and sewerage
                        <br />• No council tax payable
                        <br />• Tenure: Leisure Licence
                        <br />• Fully residential
                        <br /><br />
                        <strong>Contact:</strong> 01754 874455
                      </>
                    ) : property.id === 4 || property.id === 5 ? (
                      <>
                        Poplar Farm Leisure Park at Sutton on Sea is a family run holiday park. We have an established, friendly community of park home owners who come to us to relax, unwind and explore everything the local area has to offer throughout our 7 ½ month season (15th March – 1st November). We also welcome touring caravans, motorhomes and campervans. If you are in search of a coastal holiday in a peaceful location, we highly recommend that you pay us a visit.
                        <br /><br />
                        We are on the A16 that runs from Grimsby to Peterborough, this allows us easy access to Louth, Skegness, Boston, Kings Lyn, Melton Mowbray, Lincoln, Market Rasen, Grimsby, Hull and a host of cities, market towns and coastal villages across the East Lincolnshire Coast.
                        <br /><br />
                        <strong>Season:</strong> 15th March – 1st November (7½ months)
                        <br /><br />
                        <strong>Contact:</strong> 07454 666 892
                      </>
                    ) : (
                      <>
                        This property is located on {property.parkName}, a well-maintained park in {property.location}. 
                        The park offers excellent facilities and is perfectly positioned for enjoying all that the Lincolnshire coast has to offer.
                      </>
                    )}
                  </p>
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 border border-cyan-200">
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Park Features</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {property.id === 1 ? (
                        <>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Close to the beach
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Family friendly environment
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Quiet retreat setting
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Well-stocked fishing lake
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Sweeping lakeside views
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Exclusive development
                          </li>
                        </>
                      ) : property.id === 2 ? (
                        <>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Close to the beach
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Family friendly
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Quiet retreat
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Well stocked fishing lake
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Open countryside views
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Walking distance to attractions
                          </li>
                        </>
                      ) : property.id === 3 ? (
                        <>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Close to the beach
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Family friendly
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Quiet retreat
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Well stocked fishing lake
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Stunning lakeside views
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Tranquil park setting
                          </li>
                        </>
                      ) : property.id === 4 || property.id === 5 ? (
                        <>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            On site Café/bar
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Close to the Beach
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Tranquil site
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Scenic location
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Dog friendly park
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            15 minute walk to Sandilands beach
                          </li>
                        </>
                      ) : (
                        <>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Well-maintained grounds
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Secure environment
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Close to local amenities
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-cyan-600"></i>
                            Easy beach access
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Related Properties */}
            {relatedProperties.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Properties</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedProperties.map((relatedProp) => (
                    <Link 
                      key={relatedProp.id}
                      to={`/property/${relatedProp.id}`}
                      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-200 cursor-pointer"
                    >
                      <div className="w-full h-48 overflow-hidden">
                        <img 
                          src={relatedProp.images[0]} 
                          alt={relatedProp.title}
                          className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-gray-900 mb-2">{relatedProp.title}</h3>
                        <p className="text-2xl font-bold text-cyan-600 mb-2">£{relatedProp.price.toLocaleString()}</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mb-2">
                          <i className="ri-map-pin-line"></i>
                          {relatedProp.location}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span>{relatedProp.bedrooms} bed</span>
                          <span>•</span>
                          <span>{relatedProp.berth} berth</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              {/* Price & CTA */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-200">
                <p className="text-4xl font-bold text-cyan-600 mb-6">£{property.price.toLocaleString()}</p>
                
                <button
                  onClick={handleOpenEnquiry}
                  className="w-full px-6 py-4 bg-orange-500 text-white font-bold text-lg rounded-md hover:bg-orange-600 transition-colors mb-3 whitespace-nowrap cursor-pointer"
                >
                  Send Enquiry
                </button>
                
                <p className="text-center text-sm text-gray-600 mb-4">or call</p>
                <a 
                  href={property.id === 1 || property.id === 2 || property.id === 3 ? "tel:01754874455" : property.id === 4 || property.id === 5 ? "tel:07454666892" : "tel:01507525252"}
                  className="block text-center text-2xl font-bold text-gray-900 hover:text-cyan-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {property.id === 1 || property.id === 2 || property.id === 3 ? "01754 874455" : property.id === 4 || property.id === 5 ? "07454 666 892" : "01507 525252"}
                </a>
              </div>

              {/* Share */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Share this property</h3>
                <div className="flex gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
                    title="Share on Facebook"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(property.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-colors cursor-pointer"
                    title="Share on Twitter"
                  >
                    <i className="ri-twitter-fill text-xl"></i>
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(property.title)}&body=${encodeURIComponent(window.location.href)}`}
                    className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
                    title="Share via Email"
                  >
                    <i className="ri-mail-fill text-xl"></i>
                  </a>
                  <button
                    onClick={copyLink}
                    className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
                    title="Copy Link"
                  >
                    <i className="ri-file-copy-line text-xl"></i>
                  </button>
                  <button
                    onClick={printPage}
                    className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
                    title="Print Page"
                  >
                    <i className="ri-printer-line text-xl"></i>
                  </button>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 mt-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Your Gateway to Park Living by the Sea</h3>
                <div className="text-sm text-gray-700 space-y-4">
                  <p>
                    At Seaside Vouchers, we're committed to helping you enjoy the best of park lifestyle living. Whether you're searching for a permanent park home, a relaxing coastal retreat, or an investment opportunity for holiday lettings, our team is here to guide you toward your perfect seaside getaway.
                  </p>
                  
                  <p className="font-semibold text-gray-900">
                    Need help finding the right home? Reach out to us!
                  </p>
                  
                  <p>
                    Our friendly, experienced team is always happy to help. With deep industry knowledge and a genuine passion for park living, we'll work with you to find the ideal park home to suit your lifestyle and needs.
                  </p>
                  
                  <p>
                    Seaside Vouchers showcases a variety of park properties on our Seaside Voucher app to assist customers in exploring potential options. However, please be advised that Seaside Vouchers does not accept liability for the purchase or rental of of these properties. It remains the responsibility of the customer to conduct all necessary checks and due diligence before proceeding with any transaction.
                  </p>
                  
                  <p>
                    Suitability, dimensions, and other essential details should be verified directly with the relevant sellers or agents. Please note that Seaside Vouchers is not responsible for any sales or rental agreements. Interested parties should consult the current owners and/or park representatives for full and accurate information regarding the homes listed.
                  </p>
                  
                  <p>
                    Seaside Vouchers may retain personal data submitted through the website for the purpose of informing you about relevant property opportunities. For full details on how we manage and protect your personal information, please refer to our <a href="https://seasidevouchers.co.uk/privacy-policy/" className="text-cyan-600 hover:text-cyan-700 underline cursor-pointer">Privacy Policy</a>.
                  </p>
                  
                  <p>
                    By submitting your information through our forms, you consent to our Terms and Conditions, accept our use of cookies, and agree that your enquiry may be shared with the relevant park operator.
                  </p>
                  
                  <p>
                    In some cases, parks may display images of homes that are not physically present on-site, such as off-plan units. These could include manufacturer images or computer-generated visuals. Images may also depict homes in different park environments than the one listed. Please contact one of our agents to confirm whether a specific listing is currently available on site or is to be built.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enquiry Form Modal */}
      {showEnquiryForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">Send Enquiry</h3>
              <button
                onClick={handleCloseEnquiry}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Property Details */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-4 mb-6 border border-cyan-200">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Property Details</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Title:</strong> {property.title}</p>
                  <p><strong>Price:</strong> £{property.price.toLocaleString()}</p>
                  <p><strong>Location:</strong> {property.location}</p>
                  <p><strong>Type:</strong> {property.propertyType}</p>
                </div>
              </div>

              {/* Enquiry Form */}
              <form id="property-enquiry-form" data-readdy-form onSubmit={handleSubmitEnquiry} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm resize-none"
                    placeholder="Enter your full address"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.address.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telephone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                    <div>
                      <p className="font-semibold text-green-900">Enquiry Sent Successfully!</p>
                      <p className="text-sm text-green-700">We'll get back to you soon.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-3">
                    <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                    <div>
                      <p className="font-semibold text-red-900">Submission Failed</p>
                      <p className="text-sm text-red-700">Please try again later.</p>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseEnquiry}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-orange-500 text-white font-medium rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-cyan-500 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://seasidevouchers.co.uk/wp-content/uploads/2024/11/Group-1000005662-1024x974.png" 
                alt="Seaside Vouchers" 
                className="h-20 w-20 transform hover:scale-110 transition-transform duration-300"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-10"
              />
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Quick Link</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Home</Link></li>
                <li><Link to="/how-it-works" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">How it works</Link></li>
                <li><Link to="/seaside-offers" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Seaside offers</Link></li>
                <li><Link to="/voucher-book" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Voucher Book</Link></li>
                <li><Link to="/leisure-home-sales" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Leisure Home Sales</Link></li>
                <li><Link to="/accommodation" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Accommodation</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://seasidevouchers.co.uk/privacy-policy/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Privacy &amp; cookie policy</a></li>
                <li><a href="https://seasidevouchers.co.uk/terms-conditions/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">T&amp;C</a></li>
                <li><Link to="/contact" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Contact us</Link></li>
                <li><Link to="/business-registration" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Add Your Business</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Follow Us</h4>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/people/seasidevouchers/61566031950810/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/seaside_vouchers/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://www.tiktok.com/@seasidevouchers" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-tiktok-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 text-center text-sm">
            <p>Copyright © 2023 Seaside Voucher, All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
